import { useState } from 'react';
import { useI18n } from '../../../../../i18n';
import { useCurrentUser } from '../../../../../hooks/useCurrentUser';
import { type PersonaDetalle } from '../../../../shared/services/api';
import { cardStyle, type RolGobernanzaItem } from './persona-detail-shared';
import { AssignRolModal } from './AssignRolModal';
import { C } from '../../../../../lib/design';

const ROL_COLORS: Record<string, string> = {
  DPO: '#1D4ED8',
  OFICIAL_CUMPLIMIENTO: '#16A34A',
  PROPIETARIO_RIESGO: '#DC2626',
  AUDITOR_INTERNO: '#7C3AED',
  RESPONSABLE_CALIDAD: '#C2410C',
  PROPIETARIO_PROCESO: C.celeste,
  MIEMBRO_CONSEJO: '#B45309',
  SECRETARIO_CONSEJO: '#D97706',
  PERSONALIZADO: '#6B7280',
};

const ROL_LABELS: Record<string, string> = {
  PROPIETARIO_PROCESO: 'Propietario de Proceso',
  DPO: 'Delegado de Proteccion de Datos',
  OFICIAL_CUMPLIMIENTO: 'Oficial de Cumplimiento',
  PROPIETARIO_RIESGO: 'Propietario de Riesgo',
  AUDITOR_INTERNO: 'Auditor Interno',
  RESPONSABLE_CALIDAD: 'Responsable de Calidad',
  MIEMBRO_CONSEJO: 'Miembro del Consejo',
  SECRETARIO_CONSEJO: 'Secretario del Consejo',
  PERSONALIZADO: 'Personalizado',
};

function RolBadge({ tipoRol, nombre }: { tipoRol: string; nombre?: string | null }) {
  const color = ROL_COLORS[tipoRol] ?? '#6B7280';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 600,
        background: `${color}14`,
        color,
        border: `1px solid ${color}30`,
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
      {nombre ?? ROL_LABELS[tipoRol] ?? tipoRol}
    </span>
  );
}

export function PersonaGobernanzaTab({
  data,
  personaId,
  onRefresh,
}: {
  data: PersonaDetalle;
  personaId: string;
  onRefresh?: () => void;
}) {
  const { t } = useI18n();
  const { isAdmin } = useCurrentUser();
  const [showAssignRol, setShowAssignRol] = useState(false);
  const gobData = data.rolesGobernanza as unknown as
    | { porPuesto?: RolGobernanzaItem[]; directos?: RolGobernanzaItem[] }
    | RolGobernanzaItem[];
  const porPuesto: RolGobernanzaItem[] = Array.isArray(gobData)
    ? gobData
    : (gobData?.porPuesto ?? []);
  const directos: RolGobernanzaItem[] = Array.isArray(gobData) ? [] : (gobData?.directos ?? []);
  const isEmpty = porPuesto.length === 0 && directos.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {isAdmin && (
        <div>
          <button
            onClick={() => setShowAssignRol(true)}
            style={{
              padding: '6px 14px',
              fontSize: 12,
              fontWeight: 600,
              fontFamily: 'inherit',
              border: '1px solid #2F5496',
              borderRadius: 6,
              background: C.azul2,
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            + Asignar rol directo
          </button>
        </div>
      )}
      {showAssignRol && (
        <AssignRolModal
          personaId={personaId}
          onClose={() => setShowAssignRol(false)}
          onAssigned={() => {
            setShowAssignRol(false);
            onRefresh?.();
          }}
        />
      )}
      {isEmpty ? (
        <div style={{ ...cardStyle, color: '#9CA3AF', fontSize: 13 }}>
          {t.people.noGovernanceRoles ?? 'Sin roles de gobernanza asignados'}
        </div>
      ) : (
        <>
          {/* Roles por puesto */}
          {porPuesto.length > 0 && (
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 12px', fontSize: 14, color: C.azul1 }}>
                {t.people.rolesByPosition ?? 'Roles por puesto'}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {porPuesto.map((r) => (
                  <div
                    key={r.id}
                    style={{
                      padding: '10px 14px',
                      background: '#F8FAFC',
                      borderRadius: 6,
                      border: '1px solid #E2E8F0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 8,
                    }}
                  >
                    <RolBadge tipoRol={r.tipoRol} nombre={r.nombrePersonalizado} />
                    <span style={{ fontSize: 11, color: '#6B7280' }}>
                      via puesto: {r.puestoTitulo ?? r.ambito ?? ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Roles directos */}
          {directos.length > 0 && (
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 12px', fontSize: 14, color: C.azul1 }}>
                {t.people.directRoles ?? 'Roles directos'}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {directos.map((r) => (
                  <div
                    key={r.id}
                    style={{
                      padding: '10px 14px',
                      background: '#F8FAFC',
                      borderRadius: 6,
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 8,
                      }}
                    >
                      <RolBadge tipoRol={r.tipoRol} nombre={r.nombrePersonalizado} />
                      <span
                        style={{
                          fontSize: 11,
                          padding: '2px 8px',
                          borderRadius: 4,
                          background: r.activo ? '#ECFDF5' : '#FEF2F2',
                          color: r.activo ? '#16A34A' : '#DC2626',
                          fontWeight: 600,
                        }}
                      >
                        {r.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                    <div
                      style={{
                        marginTop: 6,
                        fontSize: 11,
                        color: '#6B7280',
                        display: 'flex',
                        gap: 16,
                        flexWrap: 'wrap',
                      }}
                    >
                      {r.organizacion && <span>Org: {r.organizacion.nombre}</span>}
                      {r.departamento && <span>Dept: {r.departamento.nombre}</span>}
                      <span>
                        Desde: {r.fechaInicio ? new Date(r.fechaInicio).toLocaleDateString() : '—'}
                      </span>
                      {r.fechaFin && (
                        <span>Hasta: {new Date(r.fechaFin).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
